// src/components/microphone/MicrophoneDetails.js
import React, { useContext, useRef, useEffect, useState } from 'react';
import { FaTimes, FaEdit, FaSave } from 'react-icons/fa';
import { AppContext } from '../../context/AppContext';
import DirectAudioPlayer from './DirectAudioPlayer';
import './MicrophoneDetails.css';

const MicrophoneDetails = ({ mic, isActive, onActivate, onClose }) => {
  const { audioStreamData, updateMicrophoneMetadata } = useContext(AppContext);
  const canvasRef = useRef(null);
  const [volumeLevel, setVolumeLevel] = useState(0);
  const [lastActivity, setLastActivity] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(mic.name);
  const [editedLocation, setEditedLocation] = useState(mic.location);
  
  // Update volume level and activity timestamp based on audio data
  useEffect(() => {
    const audioData = audioStreamData[mic.id];
    if (audioData && audioData.volume !== undefined) {
      setVolumeLevel(audioData.volume);
      
      if (audioData.volume > 10) { // Threshold for "activity"
        setLastActivity(new Date());
      }
    }
  }, [audioStreamData, mic.id]);
  
  // Draw waveform visualization if available
  useEffect(() => {
    const audioData = audioStreamData[mic.id];
    if (!canvasRef.current || !audioData || !audioData.waveform) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const { width, height } = canvas;
    
    // Clear canvas
    ctx.clearRect(0, 0, width, height);
    
    // Draw waveform with gradient
    const waveform = audioData.waveform;
    if (!Array.isArray(waveform) || waveform.length === 0) return;
    
    const sliceWidth = width / waveform.length;
    
    // Create gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, 'rgba(76, 175, 80, 0.8)');
    gradient.addColorStop(1, 'rgba(76, 175, 80, 0.2)');
    
    ctx.beginPath();
    ctx.moveTo(0, height / 2);
    
    let x = 0;
    for (let i = 0; i < waveform.length; i++) {
      const y = (waveform[i] * height / 2) + (height / 2);
      ctx.lineTo(x, y);
      x += sliceWidth;
    }
    
    ctx.lineTo(width, height / 2);
    ctx.closePath();
    
    ctx.fillStyle = isActive ? gradient : 'rgba(200, 200, 200, 0.3)';
    ctx.fill();
    
    // Draw line on top
    ctx.beginPath();
    x = 0;
    for (let i = 0; i < waveform.length; i++) {
      const y = (waveform[i] * height / 2) + (height / 2);
      
      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
      
      x += sliceWidth;
    }
    
    ctx.strokeStyle = isActive ? '#2E7D32' : '#9E9E9E';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [audioStreamData, mic.id, isActive]);
  
  // Format timestamp for readability
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'No recent activity';
    
    const now = new Date();
    const diff = Math.floor((now - timestamp) / 1000); // difference in seconds
    
    if (diff < 60) return `${diff} seconds ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
    return `${Math.floor(diff / 3600)} hours ago`;
  };
  
  // Save microphone changes
  const handleSaveChanges = async () => {
    try {
      // Only update if values have changed
      if (editedName === mic.name && editedLocation === mic.location) {
        setIsEditing(false);
        return;
      }
      
      await updateMicrophoneMetadata(mic.id, {
        name: editedName,
        location: editedLocation
      });
      
      setIsEditing(false);
    } catch (error) {
      console.error('Failed to update microphone:', error);
    }
  };
  
  // Cancel editing
  const handleCancelEdit = () => {
    setEditedName(mic.name);
    setEditedLocation(mic.location);
    setIsEditing(false);
  };
  
  // Update form values when mic changes
  useEffect(() => {
    setEditedName(mic.name);
    setEditedLocation(mic.location);
  }, [mic]);
  
  return (
    <div className="microphone-details-panel">
      <div className="details-header">
        <h3>{isEditing ? 'Edit Microphone' : `${mic.name} Details`}</h3>
        <button className="close-button" onClick={onClose}>
          <FaTimes />
        </button>
      </div>
      
      <div className="details-content">
        {isEditing ? (
          <div className="edit-form">
            <div className="form-group">
              <label htmlFor="mic-name">Name:</label>
              <input
                id="mic-name"
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                placeholder="Microphone Name"
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="mic-location">Location:</label>
              <input
                id="mic-location"
                type="text"
                value={editedLocation}
                onChange={(e) => setEditedLocation(e.target.value)}
                placeholder="Location"
              />
            </div>
            
            <div className="edit-actions">
              <button className="btn-save" onClick={handleSaveChanges}>
                <FaSave /> Save Changes
              </button>
              <button className="btn-cancel" onClick={handleCancelEdit}>
                <FaTimes /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="details-info">
            <div className="info-row">
              <span className="info-label">Location:</span>
              <span className="info-value">{mic.location || 'Unknown'}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Status:</span>
              <span className={`info-value status-${isActive ? 'active' : 'inactive'}`}>
                {isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">ID:</span>
              <span className="info-value">{mic.id}</span>
            </div>
            <div className="info-row">
              <span className="info-label">Type:</span>
              <span className="info-value">{mic.type || 'Standard'}</span>
            </div>
            {mic.ipAddress && (
              <div className="info-row">
                <span className="info-label">IP Address:</span>
                <span className="info-value">{mic.ipAddress}</span>
              </div>
            )}
            {mic.port && (
              <div className="info-row">
                <span className="info-label">Port:</span>
                <span className="info-value">{mic.port}</span>
              </div>
            )}
            <div className="info-row">
              <span className="info-label">Last Activity:</span>
              <span className="info-value">{formatTimestamp(lastActivity || mic.lastActivity)}</span>
            </div>
            
            <button className="edit-button" onClick={() => setIsEditing(true)}>
              <FaEdit /> Edit Microphone
            </button>
          </div>
        )}
        
        <div className="visualization-container">
          <h4>Live Audio Visualization</h4>
          
          <div className="waveform-container">
            <canvas 
              ref={canvasRef}
              className="waveform-canvas-detailed" 
              width="500" 
              height="200"
            ></canvas>
            
            {!isActive && (
              <div className="waveform-placeholder">
                <button 
                  className="activate-button"
                  onClick={onActivate}
                >
                  Start Streaming
                </button>
              </div>
            )}
          </div>
          
          {isActive && (
            <div className="volume-meter">
              <div className="volume-label">Volume Level</div>
              <div className="volume-bar-container">
                <div 
                  className="volume-bar"
                  style={{ width: `${Math.min(volumeLevel, 100)}%` }}
                ></div>
              </div>
              <div className="volume-value">{Math.round(volumeLevel)}%</div>
            </div>
          )}
        </div>
        
        {/* Audio Player component */}
        <div className="audio-streamer-container">
          <h4>Live Audio Feed</h4>
          <DirectAudioPlayer audioData={audioStreamData[mic.id]} />
          <div className="audio-streamer-note">
            <small>Direct PCM streaming uses Web Audio API for real-time playback</small>
          </div>
        </div>
        
        <div className="mic-actions">
          <button 
            className="close-details-button"
            onClick={onClose}
          >
            <FaTimes className="button-icon" />
            <span>Close</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default MicrophoneDetails;